export const dashboardTourSteps = [
  // Welcome Step: Welcom
  {
    image: "/images/tours/dashboard/dashboard_tour_welcome.png",
    title: "WELCOME",
    description: (
      <>
        <p className="text-sm ">
          Welcome to Xenorize! This quick tour will highlight the main parts of
          your dashboard and show you where to find your bots, portfolio, and
          key settings so you can start confidently.
          <br />
          <br />
          You can re-open this guided tour anytime from the main menu on the
          left by selecting Product Tour (or Show tips again) if you want a
          refresher.
        </p>
      </>
    ),
  },
  // STEP 1: Main Menu
  {
    image: "/images/tours/dashboard/dashboard_tour_1.png",
    title: "MAIN MENU",
    description: (
      <>
        <span className="block mb-3">
          Use the main menu on the left of your screen to jump between the key
          functions of Xenorize.
        </span>
        <ul className="list-disc pl-4 space-y-1">
          <li>
            <strong>Dashboard</strong> – Your home base: quick overview of your
            bots and portfolio.
          </li>
          <li>
            <strong>Bot List</strong> – View, create, and manage all your
            trading bots here.
          </li>
          <li>
            <strong>Portfolio</strong> – Track your overall balance,
            performance, and open positions.
          </li>
          <li>
            <strong>Notification</strong> – See important alerts about bot
            actions and market moves.
          </li>
          <li>
            <strong>Learn</strong> – Access bite-sized lessons to grow your
            trading skills.
          </li>
          <li>
            <strong>Account</strong> – Manage your profile, plan, and connected
            exchanges.
          </li>
          <li>
            <strong>Settings</strong> – Fine-tune how Xenorize works and what
            you see.
          </li>
        </ul>
      </>
    ),
  },

  // STEP 2: Wallet Connection
  {
    image: "/images/tours/dashboard/dashboard_tour_2.png", // Verify this path
    title: "WALLET CONNECTION",
    description: (
      <>
        <ol className="list-decimal pl-4 text-sm  space-y-4 mb-4">
          <li>
            <p className="text-sm ">
              Connect your exchange wallet to Xenorize so our bots can read your
              balances and run the strategies you set.
            </p>
            <p className="mt-2 text-sm  text-gray-500">
              Choose your exchange, then use Fast Connect (where available) or
              Connect with API to link your account.
            </p>
            <p className="mt-2 text-sm  text-gray-500">
              Click More Wallet to see the full list of supported exchanges.
            </p>
          </li>
          <li>
            <p className="text-sm ">
              Follow these steps to connect via API. Log in to your exchange,
              enable API access with the IPs provided, then paste your API key,
              secret, and (if required by your exchange) passphrase here.
            </p>
          </li>
        </ol>
        <p className="text-sm ">
          Xenorize uses these keys only to read your balances and orders to run
          your strategies — your crypto always stays on your exchange account.
        </p>
      </>
    ),
  },

  // STEP 3: Activate Bot
  {
    image: "/images/tours/dashboard/dashboard_tour_3.png", // Verify this path
    title: "ACTIVATE BOT",
    description: (
      <>
        <ol className="list-decimal pl-4 space-y-4 mb-4">
          <li>
            Use this section to activate your first trading bot. Click Activate
            New Bot to choose a strategy that will run using your connected
            exchange wallet.
          </li>
          <li>
            Browse the available bots and pick one that fits your goal. Click
            learn more to see what the bot is, how it trades, why it’s
            different, its key benefits, and a simple setup guide. When you’re
            ready, click <strong>Activate The Bot</strong> and follow the steps.
          </li>
        </ol>
        <p className="mt-4 text-sm pt-2 border-t border-gray-100">
          <strong>Note:</strong> Make sure you have enough balance in your
          connected exchange. For each bot, check the trading pair (e.g.
          BTC/USDT) — the bot will use the quote or base currency from that pair
          (in this case USDT) from your exchange wallet when it runs.
        </p>
      </>
    ),
  },

  // STEP 4: Asset Overview
  {
    image: "/images/tours/dashboard/dashboard_tour_4.png", // Verify this path
    title: "ASSET OVERVIEW",
    description: (
      <>
        <p className="mb-4 text-sm ">
          This overview brings everything together in one place: your total
          assets across connected exchanges, daily profit and longer-term profit
          trends from your bots, plus how each bot is performing and which ones
          are doing the best.
        </p>
        <p className="text-sm ">
          <strong>Note:</strong> Use the Filter by dropdown to switch between
          all exchanges or a specific one and quickly see how your overall
          strategy is doing.
        </p>
      </>
    ),
  },
];

export const botListTourSteps = [
  {
    image: "/images/tours/bot-list/bot_list_tour_1.png",
    title: "BOT MENU",
    description: (
      <ol className="list-decimal pl-4 space-y-4 text-sm">
        <li>
          <p>
            <strong>Active bot</strong> tab lists all the bots you’ve activated.
            Each card shows the trading pair, which exchange and strategy the
            bot is using, its starting value, and current value so you can
            quickly see how it’s doing.
          </p>
          <p className="mt-2 text-gray-500">
            Use this view to monitor your active bots at a glance or tap into a
            bot card for more details and controls.
          </p>
        </li>

        <li>
          <p>
            Use the <strong>New bot</strong> tab to explore and activate new
            bots. Each card explains what the bot does and how it trades.
          </p>
          <p className="mt-2 text-gray-500">
            Tap <strong>Learn More</strong> to read a detailed explanation of
            the strategy, why it's unique, key benefits, and a simple setup
            guide.
          </p>
          <p className="mt-2 text-gray-500">
            When you're ready, click <strong>Activate The Bot</strong> to start
            running it with your connected exchange.
          </p>
        </li>
      </ol>
    ),
  },

  {
    image: "/images/tours/bot-list/bot_list_tour_2.png",
    title: "BOT OVERVIEW",
    description: (
      <div className="space-y-4 text-sm">
        <p>
          This page shows everything about one bot: how it's performing, how
          your funds are allocated, and the history of its trades and actions.
          Use it to understand what the bot is doing and decide whether to keep
          it running, adjust settings, or stop it.
        </p>

        <p>
          The charts help you see how the bot interacts with the market. The top
          chart visualizes price movement with candles and overlays the bot’s
          grid or trading range, so you can identify where it has been placing
          buy and sell orders.
        </p>

        <p>Use the time controls (15M, 1H, 4H, 1D, 1W) to zoom in or out.</p>

        <p className="text-gray-500">
          The lower chart tracks the bot’s value or profit over time, helping
          you understand whether the strategy is trending up, flat, or down.
        </p>
      </div>
    ),
  },

  {
    image: "/images/tours/bot-list/bot_list_tour_3.png",
    title: "BOT MENU",
    description: (
      <div className="space-y-4 text-sm">
        <p>
          In this section you can see how your <strong>investment</strong> in
          this bot is doing.
        </p>

        <p>
          The <strong>Investment panel</strong> compares your starting value to
          your current value and shows ROI, realized P/L, and unrealized P/L, so
          you can tell how much profit has been locked in versus still moving
          with the market.
        </p>

        <p>
          The <strong>Allocation Asset</strong> area and chart break down your
          current position, open orders, and total asset value. The settings
          summary below (capital, trade size, price range, number of steps,
          buy/sell bounce) reminds you how the bot is configured.
        </p>

        <p className="text-gray-500">
          Use <strong>Edit Settings</strong> if you want to adjust the bot’s
          configuration, or <strong>Stop Bot</strong> if you decide to pause
          this strategy.
        </p>
      </div>
    ),
  },

  {
    image: "/images/tours/bot-list/bot_list_tour_4.png",
    title: "BOT MENU",
    description: (
      <div className="space-y-4 text-sm">
        <p>This section keeps a record of everything your bot has done.</p>

        <ol className="list-decimal pl-4 space-y-3">
          <li>
            <strong>Trade History</strong> lists each completed trade—pair,
            amount, profit/loss, entry/exit price, and timestamps—so you can
            review how the bot has been executing your strategy.
          </li>

          <li>
            <strong>Bot Log</strong> shows a timeline of the bot’s actions and
            system messages (orders placed, grid updates, errors). This helps
            you understand the bot’s decisions and troubleshoot if anything
            looks off.
          </li>
        </ol>

        <p className="text-gray-500">
          Use the pagination controls at the bottom to move through past
          records.
        </p>
      </div>
    ),
  },
];
